import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTimes,
  faGraduationCap,
  faUsers,
  faArrowLeft,
  faCrown,
  faStar,
  faHardDrive,
  faRocket,
  faTag,
  faTicketAlt
} from "@fortawesome/free-solid-svg-icons";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from '../../Helpers/toastHelper';
import { storageHelper } from '../../Helpers/storageHelper';
import Loader from "../Shared/Loader";
import MiniLoader from "../Shared/MiniLoader";
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";
import { useNavigate } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const getPlanStyle = (plan) => {
  const name = plan.name.toLowerCase();
  if (name.includes("أساسية") || name.includes("basic")) {
    return { icon: faGraduationCap, color: "#6366f1", bg: "#f5f3ff" };
  } else if (name.includes("متقدمة") || name.includes("advanced")) {
    return { icon: faRocket, color: "#8b5cf6", bg: "#f5f3ff" };
  } else if (name.includes("احترافية") || name.includes("professional") || name.includes("vip")) {
    return { icon: faCrown, color: "#6366f1", bg: "#fffbeb" };
  }
  return { icon: faStar, color: "#10b981", bg: "#ecfdf5" };
};

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostUsedPlanId, setMostUsedPlanId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await ApiServices.get("Website/Plans/getAll");
      setPlans(response.data);
      setMostUsedPlanId(response.dynamic_data);
    } catch (error) {
      toastHelper.error("فشل تحميل الباقات");
    } finally {
      setLoading(false);
    }
  };


  if (loading) return <div className="pr-root"><Header /><div className="pr-loader-box"><Loader /></div></div>;

  return (
    <div className="pr-root" dir="rtl">
      <Header />

      {/* Hero Section */}
      <section className="pr-hero">
        <div className="pr-container">
          <motion.div
            className="pr-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="pr-eyebrow" variants={fadeUp} custom={0}>
              <span className="pr-eyebrow-dot" />
              باقات الاشتراك
            </motion.div>
            <motion.h2 className="pr-title" variants={fadeUp} custom={1}>
              استثمر في <em>مستقبلك التعليمي</em>
            </motion.h2>
            <motion.p className="pr-subtitle" variants={fadeUp} custom={2}>
              اختر الباقة التي تناسب احتياجاتك وابدأ رحلة التعلم المتميز مع أفضل المعلمين والأدوات التعليمية الذكية.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="pr-section">
        <div className="pr-container">
          <div className="pr-grid">
            {plans.map((plan, index) => {
              const style = getPlanStyle(plan);
              const isPopular = plan.id === mostUsedPlanId;
              const isVip = plan.is_vip;

              return (
                <motion.div
                  key={plan.id}
                  className={`pr-card ${isPopular ? "is-popular" : ""} ${isVip ? "is-vip" : ""}`}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={index}
                >
                  {isPopular && (
                    <div className="pr-popular-badge">
                      <FontAwesomeIcon icon={faStar} />
                      الأكثر طلباً
                    </div>
                  )}
                  {isVip && (
                    <div className="pr-vip-badge">
                      <FontAwesomeIcon icon={faCrown} />
                      VIP
                    </div>
                  )}

                  <div className="pr-card-header">
                    <div className="pr-icon-box" style={{ background: `${style.color}10`, color: style.color }}>
                      <FontAwesomeIcon icon={style.icon} />
                    </div>
                    <h3 className="pr-plan-name">{plan.name}</h3>
                    <p className="pr-plan-period">{plan.period_name}</p>
                  </div>

                  <div className="pr-price-box">
                    <div className="pr-main-price">
                      {plan.discount > 0 ? (
                        <>
                          <span className="pr-amount">{plan.price_discounted}</span>
                          <span className="pr-curr">ج.م</span>
                          <span className="pr-old">{plan.original_price}</span>
                        </>
                      ) : (
                        <>
                          <span className="pr-amount">{plan.original_price}</span>
                          <span className="pr-curr">ج.م</span>
                        </>
                      )}
                    </div>
                    {plan.discount > 0 && <div className="pr-save">وفر {plan.discount} ج.م</div>}
                  </div>

                  <div className="pr-divider" />

                  <ul className="pr-features">
                    {plan.max_storage > 0 && (
                      <li className="is-included">
                        <FontAwesomeIcon icon={faHardDrive} />
                        <span>مساحة تخزين {plan.max_storage} جيجابايت</span>
                      </li>
                    )}
                    {plan.max_users > 0 && (
                      <li className="is-included">
                        <FontAwesomeIcon icon={faUsers} />
                        <span>أقصى عدد مستخدمين: {plan.max_users}</span>
                      </li>
                    )}
                    <li className="is-included">
                      <FontAwesomeIcon icon={faCheck} />
                      <span>دعم فني مخصص 24/7</span>
                    </li>
                    {plan.features.map((f) => (
                      <li key={f.id} className={f.included ? "is-included" : "is-excluded"}>
                        <FontAwesomeIcon icon={f.included ? faCheck : faTimes} />
                        <span>{f.name}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    className="pr-btn-subscribe"
                    style={{ background: style.color }}
                    onClick={() => {
                      const userType = storageHelper.getItem("user_type");
                      if (userType === "User") {
                        toastHelper.error("عذراً، هذا النوع من الحسابات غير مسموح له بالاشتراك");
                        return;
                      }
                      let amount = plan.discount > 0 ? plan.price_discounted : plan.original_price;

                      navigate('/fawaterak-payment', {
                        state: {
                          planId: plan.id,
                          plan_id: plan.id,
                          target_entity: "subscription",
                          packageDetails: {
                            name: plan.name,
                            price: amount,
                            color: style.color,
                            period_name: plan.period_name,
                          }
                        }
                      });
                    }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>اشترك الآن</span>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </motion.button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .pr-root {
          font-family: "Zain", sans-serif;
          background: #fff;
          min-height: 100vh;
          direction: rtl;
        }

        .pr-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .pr-section { padding: 40px 0 120px; }

        /* ── Header ── */
        .pr-hero { padding: 160px 0 60px; background: #fcfcfd; border-bottom: 1px solid #f1f5f9; }
        .pr-header { text-align: center; }
        .pr-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 24px;
        }
        .pr-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: pr-pulse 2.2s ease-in-out infinite;
        }
        @keyframes pr-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .pr-title {
          font-size: clamp(2.4rem, 4.5vw, 4rem);
          font-weight: 900; color: #0f172a; margin: 0 0 20px;
          line-height: 1.18; letter-spacing: -0.025em;
        }
        .pr-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 60%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .pr-subtitle {
          font-size: 1.2rem; color: #64748b; line-height: 1.85; margin: 0 auto;
          max-width: 700px; font-weight: 500;
        }

        /* ── Grid ── */
        .pr-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 32px;
        }

        /* ── Card ── */
        .pr-card {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06);
          border-radius: 32px; padding: 48px; position: relative;
          display: flex; flex-direction: column; transition: 0.3s;
        }
        .pr-card.is-popular {
          border-color: #6366f1; border-width: 2px;
          box-shadow: 0 20px 50px rgba(99, 102, 241, 0.08);
          transform: translateY(-8px);
        }
        .pr-card:hover { border-color: rgba(15,23,42,0.12); box-shadow: 0 20px 40px rgba(0,0,0,0.03); }

        .pr-popular-badge {
          position: absolute; top: -16px; left: 50%; transform: translateX(-50%);
          background: #6366f1; color: #fff; padding: 6px 20px; border-radius: 50px;
          font-weight: 900; font-size: 0.9rem; display: flex; align-items: center; gap: 8px;
          box-shadow: 0 10px 20px rgba(99, 102, 241, 0.2);
        }

        .pr-card.is-vip {
          border-color: #6366f1; border-width: 2px;
          box-shadow: 0 20px 50px rgba(245, 158, 11, 0.08);
        }

        .pr-vip-badge {
          position: absolute; top: -16px; right: 24px;
          background: linear-gradient(135deg, #6366f1, #6366f1); color: #fff; padding: 6px 16px; border-radius: 50px;
          font-weight: 900; font-size: 0.9rem; display: flex; align-items: center; gap: 8px;
          box-shadow: 0 10px 20px rgba(245, 158, 11, 0.3);
        }

        .pr-card-header { text-align: center; margin-bottom: 32px; }
        .pr-icon-box {
          width: 64px; height: 64px; border-radius: 18px; display: flex;
          align-items: center; justify-content: center; font-size: 1.8rem; margin: 0 auto 20px;
        }
        .pr-plan-name { font-size: 1.8rem; font-weight: 900; color: #0f172a; margin: 0 0 6px; }
        .pr-plan-period { font-size: 1.05rem; color: #94a3b8; font-weight: 700; }

        .pr-price-box { text-align: center; margin-bottom: 32px; }
        .pr-main-price { display: flex; align-items: baseline; justify-content: center; gap: 6px; }
        .pr-amount { font-size: 3.8rem; font-weight: 900; color: #0f172a; line-height: 1; }
        .pr-curr { font-size: 1.2rem; font-weight: 800; color: #64748b; }
        .pr-old { font-size: 1.2rem; color: #cbd5e1; text-decoration: line-through; margin-right: 10px; }
        .pr-save { display: inline-block; background: #ecfdf5; color: #10b981; padding: 4px 14px; border-radius: 50px; font-weight: 800; font-size: 0.85rem; margin-top: 12px; }

        /* ── Coupon Area ── */
        .pr-coupon-area { margin-bottom: 32px; min-height: 52px; }
        .pr-coupon-toggle {
          width: 100%; padding: 14px; border: 1.5px dashed #e2e8f0; border-radius: 16px;
          background: transparent; color: #94a3b8; font-weight: 800; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 10px; transition: 0.3s;
        }
        .pr-coupon-toggle:hover { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }

        .pr-coupon-field { display: flex; flex-wrap: wrap; gap: 10px; }
        .pr-coupon-field input {
          flex: 1; padding: 12px 16px; border: 1.5px solid #e2e8f0; border-radius: 14px;
          font-family: inherit; font-weight: 700; outline: none; transition: 0.3s;
        }
        .pr-coupon-field input:focus { border-color: #6366f1; }
        .pr-coupon-field button {
          padding: 0 24px; background: #6366f1; color: #fff; border: none;
          border-radius: 14px; font-weight: 900; cursor: pointer;
        }
        .pr-coupon-err { width: 100%; color: #ef4444; font-size: 0.85rem; font-weight: 800; margin-top: 6px; padding-right: 8px; }
        
        .pr-coupon-applied {
          display: flex; align-items: center; justify-content: space-between;
          background: #ecfdf5; border: 1.5px solid #10b981; padding: 12px 20px; border-radius: 16px;
        }
        .pr-applied-info { display: flex; align-items: center; gap: 10px; color: #065f46; font-weight: 800; font-size: 0.95rem; }
        .pr-coupon-remove { background: none; border: none; color: #ef4444; font-weight: 900; cursor: pointer; padding: 0; }

        .pr-divider { height: 1px; border-top: 1px dashed #f1f5f9; margin-bottom: 32px; }

        .pr-features { list-style: none; padding: 0; margin: 0 0 40px; flex: 1; }
        .pr-features li { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; font-weight: 600; font-size: 1.05rem; }
        .is-included { color: #1e293b; }
        .is-included svg { color: #10b981; font-size: 0.9rem; }
        .is-excluded { color: #94a3b8; }
        .is-excluded svg { color: #ef4444; font-size: 0.9rem; }

        .pr-btn-subscribe {
          width: 100%; padding: 18px; border-radius: 18px; border: none;
          color: #fff; font-family: inherit; font-weight: 900; font-size: 1.15rem;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        .pr-loader-box { height: 70vh; display: flex; align-items: center; justify-content: center; }

        @media (max-width: 768px) {
          .pr-hero { padding-top: 140px; }
          .pr-grid { grid-template-columns: 1fr; }
          .pr-card { padding: 40px 24px; }
          .pr-title { font-size: 2.2rem; }
        }
      `}</style>
    </div>
  );
};

export default Pricing;
